<script>
    import { createEventDispatcher } from 'svelte';
    import { slide } from 'svelte/transition';
    import { editorStateStore, claimsStore, specStore } from '../../stores/editorsStore';
    import { termsStore } from '../../stores/termsStore.js';
    import { validationCacheStore } from '../../stores/validationCacheStore.js';
    import { backendService } from '../../services/backendService.js';
    import { boilerplateService } from '../../services/boilerplateService';
    import { referenceService } from '../../services/referenceService';
    import { fileService, autosaveService, saveStatusStore } from '../../services/fileService';
    import { claimAssignmentService } from '../../services/claimAssignmentService';
    import { settingsStore } from '../../stores/settingsStore.js';
    import { documentMetadataStore } from '../../stores/documentMetadataStore.js';
    import BoilerplatesModal from '../boilerplates/BoilerplatesModal.svelte';
    import FiguresModal from '../figures/FiguresModal.svelte';
    import AntibodiesModal from '../tables/AntibodiesModal.svelte';
    import ChemistryModal from '../tables/ChemistryModal.svelte';
    import TablesDropdown from './TablesDropdown.svelte';
    import ClaimAssignmentModal from '../editors/claims/ClaimAssignmentModal.svelte';
    import ClaimTreeModal from '../claims/ClaimTreeModal.svelte';
    import SettingsModal from '../settings/SettingsModal.svelte';
    import PersonalizationModal from '../personalization/PersonalizationModal.svelte';
    import IDFUploadModal from '../personalization/IDFUploadModal.svelte';
    import SearchBar from './SearchBar.svelte';
    import TextFormatter from './TextFormatter.svelte';
    import ThemeToggle from '../shared/ThemeToggle.svelte';
    import PatentTypeDropdown from './PatentTypeDropdown.svelte';
    import NotificationBubble from '../shared/NotificationBubble.svelte';
    import { onMount } from 'svelte';
    import { NOTIFICATION_TIMEOUT_MS } from '../../constants/ui.js';
    import NavigationToggle from '../spec/navigation/NavigationToggle.svelte';
    import { commentsStore } from '../../stores/commentsStore.js';
    import { versionStore } from '../../stores/versionStore.js';
    import RegenerateDropdown from './RegenerateDropdown.svelte';
    import { outlineGenerationService } from '../../services/outlineGenerationService.js';

    const dispatch = createEventDispatcher();

    export let editorView; // Get spec editor view from parent
    export let claimsEditor = null; // Get claims editor view from parent
    let showFileMenu = false;
    let showVersionMenu = false;
    let isRegenerating = false;
    
    // Single notification state to ensure only one shows at a time
    let currentNotification = null;
    let notificationTimeout;
    let notificationSource = null; // Track which button triggered the notification
    
    let showBoilerplatesModal = false;
    let showFiguresModal = false;
    let showAntibodiesModal = false;
    let showChemistryModal = false;
    let showClaimAssignmentModal = false;
    let showClaimTreeModal = false;
    let showSettingsModal = false;
    let showPersonalizationModal = false;
    let showIDFUploadModal = false;
    let searchBarComponent;
    let isSearchExpanded = false;
    
    $: saveStatus = $saveStatusStore;
    
    // Get file button appearance based on save status
    function getFileButtonConfig(status) {
        switch (status) {
            case 'saving':
                return {
                    icon: 'fa-spinner fa-spin',
                    text: 'Saving...',
                    class: 'saving'
                };
            case 'saved':
                return {
                    icon: 'fa-check',
                    text: 'Saved',
                    class: 'saved'
                };
            case 'error':
                return {
                    icon: 'fa-exclamation-triangle',
                    text: 'Error',
                    class: 'error'
                };
            default:
                return {
                    icon: 'fa-file',
                    text: 'File',
                    class: ''
                };
        }
    }
    
    $: fileButtonConfig = getFileButtonConfig(saveStatus);
    
    // Export a function to focus the search bar
    export function focusSearchBar() {
        if (searchBarComponent && searchBarComponent.focusSearchBar) {
            searchBarComponent.focusSearchBar();
        }
    }
    
    // Handle search state changes from SearchBar
    function handleSearchStateChange(event) {
        isSearchExpanded = event.detail.active;
    }

    function handleFileButtonClick(e) {
        e.stopPropagation();
        showFileMenu = !showFileMenu;
    }

    function handleClickOutside(event) {
        // Don't close menus if clicking inside a portal element
        if (event.target.closest('.diff-panel')) {
            return;
        }
        showFileMenu = false;
        showVersionMenu = false;
    }

    function handleKeydown(e) {
        if (e.key === 'Escape') {
            showFileMenu = false;
            showVersionMenu = false;
        }
    }
    
    function handleSettingsClick(e) {
        e.stopPropagation();
        showSettingsModal = true;
    }

    function handlePersonalizationClick(e) {
        e.stopPropagation();
        showPersonalizationModal = true;
    }

    function handlePersonalizationSubmit(event) {
        const { customerFile, drafterFile } = event.detail;
        console.log('Personalization files submitted:', {
            customer: customerFile?.name,
            drafter: drafterFile?.name
        });
        // TODO: Implement file upload logic
        showPersonalizationModal = false;
    }

    // Single notification function that replaces any existing notification
    function showNotification(message, source = null) {
        // Clear any existing timeout
        if (notificationTimeout) clearTimeout(notificationTimeout);
        
        // Set the new notification
        currentNotification = message;
        notificationSource = source;
        
        // Set timeout to hide notification
        notificationTimeout = setTimeout(() => {
            currentNotification = null;
            notificationSource = null;
        }, NOTIFICATION_TIMEOUT_MS);
    }

    async function handleRegenerateSpec(event = null) {
        try {
            const claims = $claimsStore.content;
            
            // Filter out inline comment lines (starting with #) before checking
            const actualClaims = claims.split('\n')
                .filter(line => !line.trimStart().startsWith('#'))
                .join('\n')
                .trim();
            
            if (!actualClaims) {
                showNotification('Please add claims', 'regenerate');
                return;
            }
            
            // Check for validation errors before proceeding - use cache if available
            let validationResult = validationCacheStore.getCachedValidation(claims);
            
            if (!validationResult && !validationCacheStore.isValidating()) {
                // No cache and not currently validating - fetch fresh validation
                try {
                    validationCacheStore.setValidating(true);
                    validationResult = await backendService.validateClaims(actualClaims);
                    validationCacheStore.setCachedValidation(claims, validationResult);
                } catch (error) {
                    console.error('Error validating claims:', error);
                    validationCacheStore.setValidating(false);
                    // Continue without validation if backend is busy
                }
            }
            
            if (validationResult?.validation_errors) {
                // Update the claims editor with validation errors
                if (window.updateClaimsValidationErrors) {
                    window.updateClaimsValidationErrors(validationResult.validation_errors);
                }
                
                const hasBlockingError = validationResult.validation_errors.some(err => err.blocking);
                if (hasBlockingError) {
                    showNotification('Blocking errors. Check errors and warnings', 'regenerate');
                    return;
                }
            }

            // If event is provided from dropdown, handle outline creation
            if (event && event.detail) {
                const { selectedSections, regenerativeSections, outlineSections, allSectionDetails } = event.detail;
                
                console.log('Creating outline with sections:', selectedSections);
                
                // Create the outline (headers and regenerative blocks)
                if (editorView) {
                    const outlineResult = outlineGenerationService.createOutline(editorView, selectedSections);
                    
                    if (!outlineResult.success) {
                        console.error('Failed to create outline:', outlineResult.errors);
                        showNotification('Error creating outline', 'regenerate');
                        return;
                    }
                    
                    console.log('Outline creation result:', outlineResult);
                    
                    // Check if we created new sections or just need to regenerate existing ones
                    const createdNewSections = outlineResult.sectionsCreated.length > 0;
                    
                    // FIRST CALL: If we created new sections, just show success and don't regenerate
                    if (createdNewSections) {
                        showNotification('Outline created! Click again to generate content', 'regenerate');
                        return;
                    }
                    
                    // SUBSEQUENT CALLS: If no new sections were created, proceed with regeneration
                    // Check if any regenerative sections exist
                    if (regenerativeSections.length === 0) {
                        showNotification('No regenerative sections selected', 'regenerate');
                        return;
                    }

                    // Check if there are regenerative blocks OR title heading to regenerate
                    const specContent = editorView.getHTML();
                    const hasRegenerativeBlocks = specContent.includes('data-block-type="regenerative-support"');
                    const hasTitleHeading = specContent.includes('data-section-type="title"');
                    const hasTitleContext = regenerativeSections.some(section => section.context === 'title');

                    if (!hasRegenerativeBlocks && !(hasTitleHeading && hasTitleContext)) {
                        showNotification('No regenerative blocks or title found', 'regenerate');
                        return;
                    }
                }
            } else {
                // Legacy behavior - check if there are any regenerative blocks
                const specContent = $specStore.content;
                const hasRegenerativeBlocks = specContent.includes('data-block-type="regenerative-support"');
                
                if (!hasRegenerativeBlocks) {
                    showNotification('Please add a regenerative text block', 'regenerate');
                    return;
                }
            }

            // Proceed with regeneration - extract contexts from selected regenerative sections
            const contextsToRegenerate = event.detail.regenerativeSections.map(section => section.context);
            console.log('[DEBUG-TOOLBAR] Regenerating with contexts:', contextsToRegenerate);
            console.log('[DEBUG-TOOLBAR] Full regenerativeSections:', event.detail.regenerativeSections);
            console.log('[DEBUG-TOOLBAR] Has title context?', contextsToRegenerate.includes('title'));
            
            isRegenerating = true;
            
            // Set up a subscription to the spec store to detect when regeneration is complete
            let unsubscriber = null;
            unsubscriber = specStore.subscribe(state => {
                // Update isRegenerating based on the store's isRegenerating value
                isRegenerating = state.isRegenerating;
                
                // Clean up subscription once regeneration is complete (when isRegenerating becomes false)
                if (!state.isRegenerating && unsubscriber) {
                    unsubscriber();
                }
            });
            
            // Dispatch the event to start regeneration with context information
            dispatch('regenerateSpec', { contexts: contextsToRegenerate });
        } catch (error) {
            console.error('Error in handleRegenerateSpec:', error);
            isRegenerating = false;
        }
    }

    // Export the save function for keyboard shortcut access
    export async function handleSave(isQuickSave = false) {
        console.log('HandleSave called:', { isQuickSave, hasFileHandle: !!autosaveService.fileHandle });
        
        // Set saving status
        saveStatusStore.set('saving');
        
        try {
            if (isQuickSave) {
                // Quick save: use existing file handle if available, otherwise show dialog
                await fileService.quickSave($specStore.content, $claimsStore.content);
            } else {
                // Menu save: for now, also use existing handle, but we could force dialog for "Save As" behavior
                // For most users, they expect Save button to just save to the current file
                await fileService.saveToFile($specStore.content, $claimsStore.content, false);
            }
            
            // Save succeeded - set saved status (will auto-hide after 2 seconds via fileService)
            saveStatusStore.set('saved');
            
            // Auto-hide saved status after 2 seconds
            setTimeout(() => {
                const currentStatus = $saveStatusStore;
                if (currentStatus === 'saved') {
                    saveStatusStore.set('idle');
                }
            }, 2000);
        } catch (error) {
            console.error('Error saving file:', error);
            saveStatusStore.set('error');
            
            // Auto-hide error status after 3 seconds
            setTimeout(() => {
                const currentStatus = $saveStatusStore;
                if (currentStatus === 'error') {
                    saveStatusStore.set('idle');
                }
            }, 3000);
        }
    }

    // Save As function that forces the save dialog
    async function handleSaveAs() {
        console.log('Save As requested');
        
        // Set saving status
        saveStatusStore.set('saving');
        
        try {
            // Force save dialog by passing forceDialog=true
            await fileService.saveToFile($specStore.content, $claimsStore.content, true);
            
            // Save succeeded - set saved status
            saveStatusStore.set('saved');
            
            // Auto-hide saved status after 2 seconds
            setTimeout(() => {
                const currentStatus = $saveStatusStore;
                if (currentStatus === 'saved') {
                    saveStatusStore.set('idle');
                }
            }, 2000);
        } catch (error) {
            console.error('Error saving file:', error);
            saveStatusStore.set('error');
            
            // Auto-hide error status after 3 seconds
            setTimeout(() => {
                const currentStatus = $saveStatusStore;
                if (currentStatus === 'error') {
                    saveStatusStore.set('idle');
                }
            }, 3000);
        }
    }

    async function handleOpen() {
        try {
            const content = await fileService.loadFromFile();
            
            // Update the stores with new content
            console.log('Loading file - updating stores with:', { 
                specLength: content.spec?.length || 0, 
                claimsLength: content.claims?.length || 0,
                claimsContent: content.claims,
                claimsType: typeof content.claims
            });
            
            console.log('Before claimsStore.set - current store:', $claimsStore);
            
            // First, update the content stores
            specStore.set({ content: content.spec });
            claimsStore.set({ content: content.claims });
            console.log('After claimsStore.set - new store:', $claimsStore);
            
            // Now load terms AFTER claims content is set
            if (content.termsToLoad) {
                console.log('Loading terms after claims are set:', content.termsToLoad);
                setTimeout(() => {
                    termsStore.update(store => ({
                        ...store, // Keep existing structure
                        termEntries: new Map(content.termsToLoad.termEntries || []),
                        selectedEntries: new Set(content.termsToLoad.selectedEntries || []),
                        highlightedEntries: new Set(content.termsToLoad.highlightedEntries || []),
                        expandedEntries: new Set(content.termsToLoad.expandedEntries || []),
                        manualTerms: new Set(content.termsToLoad.manualTerms || []),
                        preservedTerms: new Set(content.termsToLoad.preservedTerms || []),
                        sortMode: content.termsToLoad.sortMode || 'appearance',
                        editingEntry: null,
                        isTermAddingMode: false,
                        isAnalyzingTerms: false
                    }));
                }, 100); // Small delay to ensure claims editor has updated
            } else {
                console.log('No terms to load');
                // Clear terms but keep the store structure intact
                termsStore.update(store => ({
                    ...store,
                    termEntries: new Map(),
                    selectedEntries: new Set(),
                    highlightedEntries: new Set(),
                    expandedEntries: new Set(),
                    manualTerms: new Set(),
                    preservedTerms: new Set(),
                    editingEntry: null
                }));
            }
            
            // Notify that content has been loaded
            dispatch('contentLoaded', { 
                spec: content.spec, 
                claims: content.claims 
            });
        } catch (error) {
            console.error('Error loading file:', error);
        }
    }

    async function handleWordImport() {
        try {
            const content = await fileService.importFromWordDocument();
            
            // Update the stores with imported content
            console.log('Importing Word document - updating stores with:', { 
                specLength: content.spec?.length || 0, 
                claimsLength: content.claims?.length || 0,
                claimsContent: content.claims,
                claimsType: typeof content.claims
            });
            
            // Update the content stores
            specStore.set({ content: content.spec });
            claimsStore.set({ content: content.claims });
            
            // Trigger notification for successful import
            showNotification('Word document imported successfully', 'file');
            
            // Notify that content has been loaded
            dispatch('documentLoaded', { importedFromWord: true });
            
        } catch (error) {
            console.error('Error importing Word document:', error);
            // Error handling is done in the fileService.importFromWordDocument method
        }
    }

    // Create clean copy - clears all content and starts fresh
    async function handleCreateCleanCopy() {
        console.log('Creating clean copy');
        
        // Clear autosave file handle to force "Save As" behavior
        autosaveService.clearFileHandle();
        
        // Clear all content stores
        specStore.set({ content: '' });
        claimsStore.set({ content: '' });
        
        // Clear terms store
        termsStore.update(store => ({
            ...store,
            termEntries: new Map(),
            selectedEntries: new Set(),
            highlightedEntries: new Set(),
            expandedEntries: new Set(),
            manualTerms: new Set(),
            preservedTerms: new Set(),
            editingEntry: null,
            isTermAddingMode: false,
            isAnalyzingTerms: false
        }));
        
        // Clear version history (both store and localStorage)
        versionStore.reset();
        localStorage.removeItem('patdraft_versions');
        
        // Reset document metadata to defaults
        documentMetadataStore.reset();
        
        // Reset all settings to defaults (including patent type history)
        settingsStore.reset();
        
        // Clear comments
        commentsStore.clearAll();
        
        // Notify parent that new clean content has been loaded
        dispatch('contentLoaded', { 
            spec: '', 
            claims: '' 
        });
        
        // Show notification
        showNotification('New document created', 'file');
    }

    function handleSearch(e) {
        dispatch('search', e.detail);
    }
    
    function handleClearSearch() {
        dispatch('clearSearch');
    }
    
    function handleSearchNavigation(e) {
        dispatch('searchNavigation', e.detail);
    }

    function handleReplace(e) {
        dispatch('replace', e.detail);
    }
    
    function handleReplaceAll(e) {
        dispatch('replaceAll', e.detail);
    }

    // Placeholder for future functionality implementation

    function handleInsertBoilerplate(event) {
        const { name, content } = event.detail;
        if (editorView) {
            // Check if we're in an empty paragraph
            const state = editorView.state;
            const { $from } = state.selection;
            const isEmptyParagraph = $from.parent.content.size === 0;
            
            // If we're in an empty paragraph, delete it first to avoid extra blank lines
            if (isEmptyParagraph) {
                editorView.dispatch(state.tr.delete($from.before(), $from.after()));
            }
            
            boilerplateService.insertBoilerplate(editorView, name, content);
        }
    }

    function handleInsertFigure(event) {
        const item = event.detail;
        if (editorView) {
            // Pass through all properties from the event detail
            // This includes id, name, type, and for numerals: figureId, value
            referenceService.insertReference(editorView, item);
        }
    }
    
    function handleInsertTable(event) {
        const { id, name } = event.detail;
        if (editorView) {
            // Use unified reference service for table insertion
            referenceService.insertReference(editorView, {
                id: id,
                name: name,
                type: 'table'
            });
        }
    }

    function handleTablesDropdownSelect(event) {
        const { type } = event.detail;
        if (type === 'antibodies') {
            showAntibodiesModal = true;
        } else if (type === 'chemistry') {
            showChemistryModal = true;
        }
    }

    async function handleApplyAssignment(event) {
        const { claimToLimitationMap, limitations, claims, literalSupport, claimGroups } = event.detail;
        if (editorView) {
            try {
                const result = await claimAssignmentService.insertAssignedClaims(
                    editorView, 
                    claimToLimitationMap, // This may contain detailed_description from debug mode
                    limitations, 
                    claims,
                    literalSupport, // Pass the literal support if available
                    claimGroups // Pass the claim groups for paragraphing
                );
                
                if (!result) {
                    showNotification('Please add a Detailed Description', 'assign');
                }
            } catch (error) {
                console.error("Error applying claim assignment:", error);
                showNotification('Please add a Detailed Description', 'assign');
            }
        }
    }
    
    // Setup autosave content monitoring
    onMount(() => {
        let lastSpecContent = $specStore.content;
        let lastClaimsContent = $claimsStore.content;
        let autosaveTimer = null;
        
        const scheduleAutosave = () => {
            if (autosaveTimer) clearTimeout(autosaveTimer);
            if (autosaveService.fileHandle) {
                autosaveTimer = setTimeout(() => {
                    autosaveService.performAutosave($specStore.content, $claimsStore.content);
                }, autosaveService.autosaveInterval);
            }
        };
        
        // Monitor content changes for autosave
        const unsubscribeSpec = specStore.subscribe(($spec) => {
            if ($spec.content !== lastSpecContent) {
                lastSpecContent = $spec.content;
                autosaveService.markAsChanged();
                scheduleAutosave();
            }
        });
        
        const unsubscribeClaims = claimsStore.subscribe(($claims) => {
            if ($claims.content !== lastClaimsContent) {
                lastClaimsContent = $claims.content;
                autosaveService.markAsChanged();
                scheduleAutosave();
            }
        });
        
        // Cleanup on destroy
        return () => {
            unsubscribeSpec();
            unsubscribeClaims();
            if (autosaveTimer) clearTimeout(autosaveTimer);
        };
    });

</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleKeydown} />

<div class="toolbar" class:search-expanded={isSearchExpanded} data-testid="toolbar">
    <div class="toolbar-content">
        <!-- Group 1: File operations (beige) -->
        <div class="toolbar-group toolbar-group-file">
            <div class="file-menu">
                <button 
                    class="toolbar-button file-button {fileButtonConfig.class}"
                    on:click={handleFileButtonClick}
                    title="File menu"
                    aria-expanded={showFileMenu}
                    aria-haspopup="true"
                    data-testid="file-menu-button"
                >
                    <i class="fas {fileButtonConfig.icon}"></i>
                    <span class="button-text">{fileButtonConfig.text}</span>
                </button>
                {#if showFileMenu}
                    <div 
                        class="file-dropdown" 
                        transition:slide|local
                        role="menu"
                        tabindex="-1"
                    >
                        <button 
                            class="menu-item" 
                            on:click={() => {
                                handleCreateCleanCopy();
                                showFileMenu = false;
                            }}
                            role="menuitem"
                            data-testid="create-clean-copy-button"
                        >
                            <i class="fas fa-file"></i>
                            New
                        </button>
                        <button 
                            class="menu-item" 
                            on:click={() => {
                                handleOpen();
                                showFileMenu = false;
                            }}
                            role="menuitem"
                            data-testid="file-open-button"
                        >
                            <i class="fas fa-folder-open"></i>
                            Open
                        </button>
                        <button 
                            class="menu-item" 
                            on:click={() => {
                                handleSave();
                                showFileMenu = false;
                            }}
                            role="menuitem"
                            data-testid="file-save-button"
                        >
                            <i class="fas fa-save"></i>
                            Save
                            <span class="keyboard-shortcut">
                                {#if navigator.platform.toLowerCase().includes('mac')}⌘S{:else}Ctrl+S{/if}
                            </span>
                        </button>
                        <button 
                            class="menu-item" 
                            on:click={() => {
                                handleSaveAs();
                                showFileMenu = false;
                            }}
                            role="menuitem"
                            data-testid="file-save-as-button"
                        >
                            <i class="fas fa-copy"></i>
                            Save As...
                        </button>
                        <button 
                            class="menu-item" 
                            on:click={() => {
                                handleWordImport();
                                showFileMenu = false;
                            }}
                            role="menuitem"
                            data-testid="file-import-word-button"
                        >
                            <i class="fas fa-file-word"></i>
                            Import Word
                        </button>
                        <button 
                            class="menu-item" 
                            on:click={() => {
                                dispatch('exportToWord');
                                showFileMenu = false;
                            }}
                            role="menuitem"
                            data-testid="export-word-button"
                        >
                            <i class="fas fa-file-word"></i>
                            Export text to Word
                        </button>
                    </div>
                {/if}
            </div>
            
            <div class="version-menu">
                <button 
                    class="toolbar-button"
                    title="Version Control"
                    data-testid="version-button"
                    on:click={(e) => {
                        e.stopPropagation();
                        showVersionMenu = !showVersionMenu;
                    }}
                    class:active={showVersionMenu}
                    aria-expanded={showVersionMenu}
                    aria-haspopup="true"
                >
                    <i class="fas fa-history"></i>
                    <span class="button-text">History</span>
                    {#if $versionStore.hasUnsavedChanges}
                        <span class="unsaved-indicator">•</span>
                    {/if}
                </button>
                {#if showVersionMenu}
                    <div 
                        class="file-dropdown" 
                        transition:slide|local
                        role="menu"
                        tabindex="-1"
                    >
                        <button 
                            class="menu-item" 
                            on:click={() => {
                                const name = prompt('Enter a name for this version:');
                                if (name && name.trim()) {
                                    versionStore.saveVersion(name.trim(), $specStore.content, $claimsStore.content);
                                }
                                showVersionMenu = false;
                            }}
                            role="menuitem"
                            data-testid="save-version-button"
                        >
                            <i class="fas fa-save"></i>
                            Save Current Version
                            {#if $versionStore.hasUnsavedChanges}
                                <span class="unsaved-indicator">•</span>
                            {/if}
                        </button>
                        
                        {#if $versionStore.versions.length > 0}
                            <div style="height: 1px; margin: var(--spacing-1) 0; background-color: var(--color-gray-200);"></div>
                            
                            {#each $versionStore.versions as version (version.id)}
                                <button 
                                    class="menu-item"
                                    class:active={$versionStore.currentVersion?.id === version.id}
                                    on:click={() => {
                                        if ($versionStore.hasUnsavedChanges) {
                                            if (!confirm('You have unsaved changes. Loading another version will discard them. Continue?')) {
                                                return;
                                            }
                                        }
                                        const loadedVersion = versionStore.loadVersion(version.id);
                                        if (loadedVersion) {
                                            specStore.set({ content: loadedVersion.content.spec });
                                            claimsStore.set({ content: loadedVersion.content.claims });
                                            dispatch('contentLoaded', { 
                                                spec: loadedVersion.content.spec, 
                                                claims: loadedVersion.content.claims 
                                            });
                                        }
                                        showVersionMenu = false;
                                    }}
                                    role="menuitem"
                                >
                                    <i class="fas fa-code-branch"></i>
                                    <div style="flex: 1; text-align: left;">
                                        <div>{version.name}</div>
                                        <div style="font-size: 0.75rem; color: var(--color-text-tertiary);">
                                            {new Date(version.createdAt).toLocaleString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </div>
                                    </div>
                                    {#if $versionStore.currentVersion?.id === version.id}
                                        <i class="fas fa-check menu-item-check"></i>
                                    {:else}
                                        <button 
                                            class="delete-version-btn"
                                            on:click|stopPropagation={(e) => {
                                                if (confirm(`Delete version "${version.name}"?`)) {
                                                    versionStore.deleteVersion(version.id);
                                                }
                                            }}
                                            title="Delete version"
                                            aria-label="Delete version {version.name}"
                                        >
                                            <i class="fas fa-trash"></i>
                                        </button>
                                    {/if}
                                </button>
                            {/each}
                            
                            <div style="height: 1px; margin: var(--spacing-1) 0; background-color: var(--color-gray-200);"></div>
                            
                            <button 
                                class="menu-item" 
                                on:click={() => {
                                    dispatch('toggleDiffPanel');
                                    showVersionMenu = false;
                                }}
                                role="menuitem"
                                data-testid="compare-versions-button"
                                title="Compare with last saved version (Ctrl+D)"
                            >
                                <i class="fas fa-code-compare"></i>
                                Show Changes
                                <span class="keyboard-shortcut">Ctrl+D</span>
                            </button>
                        {:else}
                            <div style="padding: var(--spacing-4); font-size: var(--font-size-sm); text-align: center; color: var(--color-text-tertiary);">
                                No saved versions yet
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
            
            <button 
                class="toolbar-button"
                title="Comments (Ctrl+Shift+C)"
                data-testid="comments-button"
                on:click={() => commentsStore.toggle()}
            >
                <i class="fas fa-comments"></i>
                <span class="button-text">Comments</span>
            </button>
            
            <ThemeToggle />
            
            <PatentTypeDropdown />

            <button
                class="toolbar-button"
                title="Personalization"
                data-testid="personalization-button"
                on:click={handlePersonalizationClick}
            >
                <i class="fas fa-user-cog"></i>
            </button>

            <button
                class="toolbar-button settings-button"
                title="Settings"
                data-testid="settings-button"
                on:click={handleSettingsClick}
            >
                <i class="fas fa-cog"></i>
            </button>
        </div>
        
        <!-- Divider between Group 1 and Group 2 -->
        <div class="divider"></div>
        
        <!-- Group 2: Context toggle tools (pink) -->
        <div class="toolbar-group toolbar-group-context">
            <NavigationToggle />
            
            <button 
                class="toolbar-button spec-button"
                on:click={() => {
                    showBoilerplatesModal = true;
                }}
                title="View Boilerplates"
                data-testid="boilerplates-button"
            >
                <i class="fas fa-file-alt"></i>
                <span class="button-text">Boilerplates</span>
            </button>
            
            <button
                class="toolbar-button spec-button"
                on:click={() => showFiguresModal = true}
                title="View Figures"
                data-testid="figures-button"
            >
                <i class="fas fa-images"></i>
                <span class="button-text">Figures</span>
            </button>

            <button
                class="toolbar-button spec-button"
                on:click={() => showIDFUploadModal = true}
                title="Upload Invention Disclosure Form"
                data-testid="idf-upload-button"
            >
                <i class="fas fa-file-upload"></i>
                <span class="button-text">IDF</span>
            </button>

            <TablesDropdown
                on:select={handleTablesDropdownSelect}
            />

            <div class="button-with-notification">
                <RegenerateDropdown 
                    {isRegenerating}
                    on:regenerate={handleRegenerateSpec}
                />
                <NotificationBubble 
                    show={currentNotification && notificationSource === 'regenerate'}
                    message={currentNotification || ''}
                    variant={currentNotification && currentNotification.includes('Blocking errors') ? 'error' : 'spec'}
                    position="bottom"
                    duration={NOTIFICATION_TIMEOUT_MS}
                    on:hide={() => {
                        currentNotification = null;
                        notificationSource = null;
                    }}
                />
            </div>

            <div class="button-with-notification">
                <button
                    class="toolbar-button spec-button"
                    on:click={async () => {
                        const claims = $claimsStore.content;
                        
                        // Filter out inline comment lines (starting with #) before checking
                        const actualClaims = claims.split('\n')
                            .filter(line => !line.trimStart().startsWith('#'))
                            .join('\n')
                            .trim();

                        if (!actualClaims) {
                            showNotification('Please add claims first', 'assign');
                            return;
                        }
                        
                        // Check for validation errors - use cache if available
                        let validationResult = validationCacheStore.getCachedValidation(claims);
                        
                        if (!validationResult && !validationCacheStore.isValidating()) {
                            // No cache and not currently validating - fetch fresh validation
                            try {
                                validationCacheStore.setValidating(true);
                                validationResult = await backendService.validateClaims(actualClaims);
                                validationCacheStore.setCachedValidation(claims, validationResult);
                            } catch (error) {
                                console.error('Error validating claims:', error);
                                validationCacheStore.setValidating(false);
                                // Continue without validation if backend is busy
                            }
                        }
                        
                        if (validationResult?.validation_errors) {
                            // Update the claims editor with validation errors
                            if (window.updateClaimsValidationErrors) {
                                window.updateClaimsValidationErrors(validationResult.validation_errors);
                            }
                            
                            const hasBlockingError = validationResult.validation_errors.some(err => err.blocking);
                            if (hasBlockingError) {
                                showNotification('Blocking errors. Check errors and warnings', 'assign');
                                return;
                            }
                        }

                        // Check if the spec has a Detailed Description section
                        if (editorView) {
                            const { state } = editorView;
                            const { doc } = state;
                            let hasDetailedDescription = false;

                            // Visit all nodes to find a heading with section type 'detailed'
                            doc.descendants((node) => {
                                if (node.type.name === 'heading' &&
                                    node.attrs.sectionType === 'detailed') {
                                    hasDetailedDescription = true;
                                    return false; // Stop traversal once found
                                }
                            });

                            if (!hasDetailedDescription) {
                                showNotification('Please add a Detailed Description', 'assign');
                                return;
                            }
                        }

                        showClaimAssignmentModal = true;
                    }}
                    title="Assign claims to elements for specification"
                    data-testid="assign-claims-button"
                >
                    <i class="fas fa-sitemap"></i>
                    <span class="button-text">Assign Claims</span>
                </button>
                <NotificationBubble 
                    show={currentNotification && notificationSource === 'assign'}
                    message={currentNotification || ''}
                    variant="error"
                    position="bottom"
                    duration={NOTIFICATION_TIMEOUT_MS}
                    on:hide={() => {
                        currentNotification = null;
                        notificationSource = null;
                    }}
                />
            </div>

            <div class="button-with-notification">
                <button
                    class="toolbar-button spec-button"
                    on:click={async () => {
                        const claims = $claimsStore.content;
                        
                        // Filter out inline comment lines (starting with #) before checking
                        const actualClaims = claims.split('\n')
                            .filter(line => !line.trimStart().startsWith('#'))
                            .join('\n')
                            .trim();

                        if (!actualClaims) {
                            showNotification('Please add claims first', 'tree');
                            return;
                        }
                        
                        // Check for validation errors - use cache if available
                        let validationResult = validationCacheStore.getCachedValidation(claims);
                        
                        if (!validationResult && !validationCacheStore.isValidating()) {
                            // No cache and not currently validating - fetch fresh validation
                            try {
                                validationCacheStore.setValidating(true);
                                validationResult = await backendService.validateClaims(actualClaims);
                                validationCacheStore.setCachedValidation(claims, validationResult);
                            } catch (error) {
                                console.error('Error validating claims:', error);
                                validationCacheStore.setValidating(false);
                                // Continue without validation if backend is busy
                            }
                        }
                        
                        if (validationResult?.validation_errors) {
                            // Update the claims editor with validation errors
                            if (window.updateClaimsValidationErrors) {
                                window.updateClaimsValidationErrors(validationResult.validation_errors);
                            }
                            
                            const hasBlockingError = validationResult.validation_errors.some(err => err.blocking);
                            if (hasBlockingError) {
                                showNotification('Blocking errors. Check errors and warnings', 'tree');
                                return;
                            }
                        }

                        showClaimTreeModal = true;
                    }}
                    title="View claim reference trees"
                    data-testid="claim-tree-button"
                >
                    <i class="fas fa-project-diagram"></i>
                    <span class="button-text">Claim Tree</span>
                </button>
                <NotificationBubble 
                    show={currentNotification && notificationSource === 'tree'}
                    message={currentNotification || ''}
                    variant="error"
                    position="bottom"
                    duration={NOTIFICATION_TIMEOUT_MS}
                    on:hide={() => {
                        currentNotification = null;
                        notificationSource = null;
                    }}
                />
            </div>
        </div>
        
        <!-- Divider between Group 2 and Group 3 -->
        <div class="divider"></div>
        
        <!-- Group 3: Spec editing tools (pink) -->
        <div class="toolbar-group toolbar-group-spec">
            <TextFormatter specEditor={editorView} claimsEditor={claimsEditor} />
            
            <SearchBar 
                bind:this={searchBarComponent}
                on:search={handleSearch} 
                on:clearSearch={handleClearSearch}
                on:searchNavigation={handleSearchNavigation}
                on:replace={handleReplace}
                on:searchStateChange={handleSearchStateChange}
                on:replaceAll={handleReplaceAll}
                on:focusEditor={(e) => dispatch('focusEditor', e.detail)}
                on:setEditorsEditable={(e) => dispatch('setEditorsEditable', e.detail)}
            />
        </div>
    </div>
</div>

{#if showBoilerplatesModal}
    <BoilerplatesModal
        show={showBoilerplatesModal}
        on:close={() => {
            showBoilerplatesModal = false;
        }}
        on:insert={({ detail }) => {
            boilerplateService.insertBoilerplate(editorView, detail.name, detail.content);
        }}
    />
{/if}

{#if showFiguresModal}
    <FiguresModal
        show={showFiguresModal}
        on:close={() => showFiguresModal = false}
        on:insert={handleInsertFigure}
    />
{/if}
{#if showAntibodiesModal}
    <AntibodiesModal
        show={showAntibodiesModal}
        on:close={() => showAntibodiesModal = false}
        on:insert={handleInsertTable}
    />
{/if}

{#if showChemistryModal}
    <ChemistryModal
        show={showChemistryModal}
        on:close={() => showChemistryModal = false}
    />
{/if}

{#if showClaimAssignmentModal}
    <ClaimAssignmentModal
        show={showClaimAssignmentModal}
        on:close={() => showClaimAssignmentModal = false}
        on:apply={handleApplyAssignment}
    />
{/if}

{#if showClaimTreeModal}
    <ClaimTreeModal
        show={showClaimTreeModal}
        on:close={() => showClaimTreeModal = false}
    />
{/if}

{#if showSettingsModal}
    <SettingsModal
        bind:show={showSettingsModal}
        on:close={() => showSettingsModal = false}
    />
{/if}

{#if showPersonalizationModal}
    <PersonalizationModal
        show={showPersonalizationModal}
        on:close={() => showPersonalizationModal = false}
        on:submit={handlePersonalizationSubmit}
    />
{/if}

{#if showIDFUploadModal}
    <IDFUploadModal
        show={showIDFUploadModal}
        on:close={() => showIDFUploadModal = false}
        on:save={() => showIDFUploadModal = false}
    />
{/if}


<style>
/* Toolbar container - using component-scoped styles without :global() */
.toolbar {
    position: relative;
    z-index: var(--z-index-40);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 60px;
    padding: var(--spacing-3) var(--spacing-6) !important; /* Increased horizontal padding for breathing room */
    overflow: visible; /* Allow dropdowns to show */
    background-color: var(--color-bg-toolbar);
    border-radius: var(--border-radius-2xl);
    box-sizing: border-box; /* Ensure padding is included in width */
}


/* Content wrapper that centers everything */
.toolbar-content {
    display: flex;
    flex-wrap: nowrap; /* Prevent wrapping to maintain horizontal distribution */
    justify-content: center; /* Center all content */
    align-items: center;
    gap: var(--spacing-3); /* Reduced gap between groups for more compact layout */
    width: 100%; /* Take full width of toolbar minus padding */
    max-width: 100%; /* Prevent overflow */
    padding: 0 var(--spacing-2); /* Small padding to ensure edge clearance */
    overflow: visible; /* Allow dropdowns to show */
}

/* Toolbar groups for logical grouping */
.toolbar-group {
    display: flex;
    align-items: center;
    gap: var(--spacing-2); /* Compact gap within groups */
    flex-shrink: 0; /* Prevent groups from shrinking too much */
}


/* File menu components */
.file-menu {
    position: relative;
    display: inline-block; /* Prevent expansion */
}

/* Version menu components */
.version-menu {
    position: relative;
    display: inline-block; /* Prevent expansion */
}

.unsaved-indicator {
    margin-left: var(--spacing-1);
    font-size: var(--font-size-lg);
    line-height: 1;
    color: var(--color-warning, #f59e0b);
}

.file-button {
    padding: var(--spacing-2) var(--spacing-3);
}

.file-dropdown {
    position: absolute;
    top: calc(100% + var(--spacing-2));
    left: 0;
    z-index: var(--z-index-dropdown);
    min-width: 180px;
    padding: var(--spacing-2);
    background-color: var(--color-bg-file-dropdown);
    border-radius: var(--border-radius-2xl);
    box-shadow: var(--shadow-dropdown);
}

.menu-item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
    white-space: nowrap;
    color: var(--color-text-secondary);
    background: transparent;
    border: none;
    border-radius: var(--border-radius-xl);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
    gap: var(--spacing-2);
}

.menu-item:hover {
    background-color: var(--color-bg-hover-light);
}

.menu-item i {
    width: var(--spacing-4);
}

.menu-item.active {
    background-color: var(--color-bg-hover-medium);
}

.menu-item-check {
    margin-left: auto;
    color: var(--color-terms-600);
}

.keyboard-shortcut {
    margin-left: auto;
    font-size: var(--font-size-xs);
    text-transform: lowercase;
    color: var(--color-text-tertiary);
}

.delete-version-btn {
    padding: var(--spacing-1) var(--spacing-2);
    margin-left: auto;
    color: var(--color-text-tertiary);
    background: none;
    border: none;
    border-radius: var(--border-radius-md);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
}

.delete-version-btn:hover {
    color: var(--color-error);
    background-color: var(--color-error-bg);
}

/* Button styles */
.toolbar-button,
:global(.toolbar-button) {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0; /* Prevent buttons from shrinking below their content */
    min-width: fit-content; /* Ensure buttons don't shrink below content width */
    height: var(--height-toolbar-button);
    padding: 0 var(--spacing-3); /* Reduced padding for more compact buttons */
    font-size: var(--font-size-sm);
    white-space: nowrap;
    color: var(--color-text-secondary);
    background-color: var(--color-btn-default);
    border: none;
    border-radius: var(--border-radius-2xl);
    transition: background-color 0.2s var(--ease-in-out);
    cursor: pointer;
    gap: 6px; /* Reduced gap between icon and text */
    box-sizing: border-box;
}

.toolbar-button:hover,
:global(.toolbar-button:hover) {
    background-color: var(--color-btn-default-hover);
}

.toolbar-button:active,
:global(.toolbar-button:active) {
    background-color: var(--color-btn-default-active);
}

.toolbar-button:disabled,
:global(.toolbar-button:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Button text label */
.button-text,
:global(.button-text) {
    transition: opacity 0.2s var(--ease-in-out);
}

/* Spec-specific buttons */
.spec-button,
:global(.spec-button) {
    background-color: var(--color-btn-spec);
}

.spec-button:hover:not(:disabled),
:global(.spec-button:hover:not(:disabled)) {
    background-color: var(--color-btn-spec-hover);
}

.spec-button:disabled,
:global(.spec-button:disabled) {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Save status button styles */
.file-button.saving {
    color: var(--color-text-secondary);
    background-color: var(--color-surface-secondary);
}

.file-button.saved {
    color: var(--color-success);
    background-color: var(--color-success-background);
    animation: savedPulse 0.3s ease-out;
}

.file-button.error {
    color: var(--color-error);
    background-color: var(--color-error-background);
}

@keyframes savedPulse {
    0% { 
        background-color: var(--color-success); 
        transform: scale(1);
    }

    50% { 
        background-color: var(--color-success); 
        transform: scale(1.05);
    }

    100% { 
        background-color: var(--color-success-background); 
        transform: scale(1);
    }
}

/* Button with notification wrapper */
.button-with-notification {
    position: relative;
}

/* Utility components */
.divider {
    flex: 0 0 1px; /* Prevent shrinking */
    width: 1px;
    height: var(--spacing-6);
    margin: 0; /* No margin needed, gap handles spacing */
    background-color: var(--border-color-default);
}

/* Notification styles moved to NotificationBubble component */

/* Responsive design */
@media (width > 2000px) {
    /* Extra large screens get more spacing */

    /* Toolbar padding is controlled by base .toolbar class with !important */
    
    .toolbar-content {
        gap: var(--spacing-6); /* Moderate gap for very wide screens */
    }
    
    .toolbar-group {
        gap: var(--spacing-3); /* Moderate gap within groups */
    }
}

@media (width > 1600px) and (width <= 2000px) {
    /* Large screens get moderate spacing */

    /* Toolbar padding is controlled by base .toolbar class with !important */
    
    .toolbar-content {
        gap: var(--spacing-4); /* Compact gap */
    }
    
    .toolbar-group {
        gap: var(--spacing-2); /* Compact gap within groups */
    }
    
}

/* Intermediate transition - start compacting before 1600px */
@media (width <= 1700px) and (width > 1600px) {
    /* Toolbar padding is controlled by base .toolbar class with !important */
    
    .toolbar-content {
        gap: var(--spacing-4); /* Reduce gap earlier */
    }
    
    .toolbar-group {
        gap: var(--spacing-2); /* Reduce group gaps */
    }
    
    .toolbar-button {
        padding: 0 var(--spacing-3); /* Start reducing button padding */
    }
    
}

/* At minimum viewport width (1366px), ensure everything fits */
@media (width <= 1600px) and (width >= 1366px) {
    /* Toolbar padding is controlled by base .toolbar class with !important */
    
    .toolbar-button {
        padding: 0 var(--spacing-2);
    }
    
    .button-text {
        font-size: var(--font-size-xs); /* 12px */
    }
    
    .toolbar-content {
        gap: var(--spacing-3); /* Very compact gap for minimum viewport */
    }
    
    .toolbar-group {
        gap: var(--spacing-1); /* Very compact gap within groups */
    }
    
    /* Hide text for remaining spec buttons (navigation-toggle already hidden earlier) */
    .toolbar-button.spec-button:not(.navigation-toggle) .button-text {
        display: none;
    }
    
    .toolbar-button.spec-button:not(.navigation-toggle) {
        width: var(--height-toolbar-button);
        min-width: var(--height-toolbar-button);
        padding: 0;
        border-radius: 50%;
    }
    
    /* Reduce divider spacing */
    .divider {
        flex: 0 0 auto;
        margin: 0 var(--spacing-1);
    }
}

/* Below minimum viewport - no changes, just let scrollbar appear */
@media (width < 1366px) {
    /* Maintain the same styles as at 1366px */

    /* Toolbar padding is controlled by base .toolbar class with !important */
    
    .toolbar-button {
        padding: 0 var(--spacing-2);
    }
    
    .button-text {
        font-size: var(--font-size-xs);
    }
    
    .toolbar-content {
        gap: var(--spacing-3);
    }
    
    .toolbar-group {
        gap: var(--spacing-1);
    }
    
    .toolbar-button.spec-button:not(.navigation-toggle) .button-text {
        display: none;
    }
    
    .toolbar-button.spec-button:not(.navigation-toggle) {
        width: var(--height-toolbar-button);
        min-width: var(--height-toolbar-button);
        padding: 0;
        border-radius: 50%;
    }
    
    .divider {
        flex: 0 0 auto;
        margin: 0 var(--spacing-1);
    }
}

/* Dark mode button text color adjustments */
:global([data-theme="dark"]) .toolbar-button,
:global([data-theme="dark"] .toolbar-button) {
    color: var(--color-text-primary);
}


/* Compact mode when search is expanded */
.toolbar.search-expanded {
    /* Maintain edge padding even when search is expanded */
    padding: var(--spacing-3) var(--spacing-6) !important;
}

.toolbar.search-expanded .toolbar-content {
    gap: var(--spacing-3); /* Reduce from default gap */
}

.toolbar.search-expanded .toolbar-group {
    gap: var(--spacing-2); /* Tighter group spacing */
}

.toolbar.search-expanded .toolbar-button {
    padding: 0 var(--spacing-2); /* Reduce button padding */
}

/* Progressive compacting based on viewport width */
@media (width <= 1700px) {
    .toolbar.search-expanded {
        padding: var(--spacing-3) var(--spacing-5) !important; /* Slightly less padding at medium viewports */
    }
    
    .toolbar.search-expanded .toolbar-content {
        gap: var(--spacing-2);
    }
    
    .toolbar.search-expanded .toolbar-group {
        gap: var(--spacing-1);
    }
}

@media (width <= 1600px) {
    .toolbar.search-expanded {
        padding: var(--spacing-2) var(--spacing-4) !important; /* Minimum edge padding */
    }
    
    /* Hide spec button text when search is expanded at medium viewports */
    .toolbar.search-expanded .spec-button .button-text {
        display: none;
    }
    
    .toolbar.search-expanded .toolbar-content {
        gap: var(--spacing-2);
    }
}

@media (width <= 1400px) {
    .toolbar.search-expanded {
        padding: var(--spacing-2) var(--spacing-3) !important; /* Even less at small viewports */
    }
    
    /* More aggressive compacting at smaller viewports */
    .toolbar.search-expanded .toolbar-content {
        gap: var(--spacing-1);
    }
    
    /* Hide history and comments text as well */
    .toolbar.search-expanded .toolbar-group-file .toolbar-button:not(.file-button) .button-text {
        display: none;
    }
}

/* Ensure critical buttons keep their text labels */
.toolbar.search-expanded .file-button .button-text,
.toolbar.search-expanded .terms-button .button-text {
    display: inline !important; /* Keep file and terms buttons text visible */
}


/* Hide other spec buttons and comments text later to prevent overflow */
@media (width <= 2000px) {
    .toolbar-button.spec-button:not(.navigation-toggle) .button-text,
    .toolbar-button[data-testid="comments-button"] .button-text {
        display: none !important;
    }
    
    .toolbar-button.spec-button:not(.navigation-toggle),
    .toolbar-button[data-testid="comments-button"] {
        width: var(--height-toolbar-button);
        min-width: var(--height-toolbar-button);
        padding: 0;
        border-radius: 50%;
    }
    
    /* Tighter spacing at 2000px */
    .toolbar-content {
        gap: var(--spacing-2);
    }
    
    .toolbar-group {
        gap: var(--spacing-1);
    }
    
    .divider {
        margin: 0 var(--spacing-1);
    }
}

</style>